import { useState } from 'react';
import { Label } from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import useTranslation from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { TranscriptsTranslationDropdownProps } from '@/types';

function TranscriptsTranslationDropdown({
  isLoading,
  activeLanguage,
  handleLanguageSelection,
  translatedLanguages,
  unTranslatedLanguages,
  onDeleteLanguage,
  pendingLanguage,
  hideLabel = false,
}: TranscriptsTranslationDropdownProps) {
  const { lang } = useTranslation();
  const [languageToDelete, setLanguageToDelete] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDeleteClick = (language: string, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setLanguageToDelete(language);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (languageToDelete && onDeleteLanguage) {
      onDeleteLanguage(languageToDelete);
    }
    setIsDeleteDialogOpen(false);
    setLanguageToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setLanguageToDelete(null);
  };

  return (
    <div className="flex flex-col gap-2 relative">
      {/* 'Language' label is hidden below 800px to save vertical space on
          the narrow transcript-detail toolbar — the dropdown itself
          already surfaces the active language so the label is redundant.
          Also hidden explicitly when `hideLabel` is set (e.g. when the
          control is rendered inside a Sheet header without vertical room
          above it, such as the responses panel). */}
      {!hideLabel && (
        <span className="absolute -top-5 left-0 text-xs font-bold md800:hidden">
          {lang.get('msg.language')}
        </span>
      )}
      <DropdownMenu onOpenChange={(open) => (isLoading ? false : open)}>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={isLoading}
            variant="ghost"
            className="flex items-center border border-gray-300 min-w-28 md800:min-w-0 md800:px-2 md800:h-9 justify-between"
            aria-label={
              isLoading ? 'Translation in progress' : `Select language, current: ${activeLanguage}`
            }
            aria-busy={isLoading}
          >
            {isLoading && (
              <Loader2 className="animate-spin mr-2 h-5 w-5" aria-hidden="true" />
            )}
            {activeLanguage}
            <ChevronDown className="w-4 h-4 ml-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-40 max-h-60 overflow-y-auto">
          {/* Translated Languages */}
          <Label className="font-semibold text-xs ml-3 mt-2 mb-1">
            {lang.get('msg.translations')}
          </Label>
          {translatedLanguages?.map((language) => {
            const isOriginal = language === 'Original';
            const isActive = language === activeLanguage;

            return (
              <DropdownMenuCheckboxItem
                key={language}
                className={`capitalize cursor-pointer ${
                  isActive ? 'bg-gray-200 cursor-auto text-dark-text pointer-events-none' : ''
                }`}
                onClick={() => handleLanguageSelection(language)}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{language}</span>
                  <div className="flex items-center gap-1">
                    {isActive && <Check className="w-4 h-4" />}
                    {!isOriginal && onDeleteLanguage && !isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={language === activeLanguage || pendingLanguage === language}
                        className="h-5 w-5 p-0 hover:bg-red-100 hover:text-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        onClick={(e) => handleDeleteClick(language, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDeleteClick(language, e);
                          }
                        }}
                        aria-label={`Delete ${language} translation`}
                        tabIndex={0}
                      >
                        <X className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              </DropdownMenuCheckboxItem>
            );
          })}

          <hr className="solid my-2"></hr>

          {/* Untranslated Languages */}
          <Label className="font-semibold text-xs ml-3 mt-2 mb-1">
            {lang.get('msg.addTranslations')}
          </Label>
          {unTranslatedLanguages?.map((language) => (
            <DropdownMenuCheckboxItem
              key={language}
              className="capitalize cursor-pointer"
              onClick={() => handleLanguageSelection(language)}
            >
              {language}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Translation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the {languageToDelete} translation? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TranscriptsTranslationDropdown;
